import type { Release } from '@sunstone/api-client/gen/releases/v1/releases_pb';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@sunstone/ui';
import { ArrowUpCircle, Boxes, Layers, Rocket } from 'lucide-react';
import type { ReactNode } from 'react';
import { VERSION_UNKNOWN_LABEL } from '@/features/workspaces/WorkspaceVersionStatus';

export type DeployDialog = 'bais' | 'mai' | 'single-service';

interface DeployTileProps {
  icon: ReactNode;
  title: string;
  description: string;
  action: ReactNode;
}

function DeployTile({ icon, title, description, action }: DeployTileProps) {
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-center gap-2">
          {icon}
          <CardTitle className="text-base">{title}</CardTitle>
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="mt-auto pt-0">{action}</CardContent>
    </Card>
  );
}

interface WorkspaceDeployUpgradePanelProps {
  currentVersion: string;
  nextRelease?: Release;
  showPreviewUpgradeButton: boolean;
  onPreviewRelease?: (releaseId: string) => void;
  hasPermission: (permission: string) => boolean;
  onOpenDeployDialog: (dialog: DeployDialog) => void;
  workflowRunning: boolean;
}

/**
 * Four uniform action tiles (icon/title/one-line description/single button,
 * same height) rather than inline forms of wildly different sizes (Single
 * Service Deploy has 6 fields, MAI has 1, BAIS has 2, Platform Upgrade has
 * none) - the actual form lives in a Dialog opened on click, so card height
 * never depends on form complexity. The Components tab's family empty-state
 * buttons open the same dialogs via the same onOpenDeployDialog/
 * onPreviewRelease callbacks passed down from WorkspaceDetail.
 */
export function WorkspaceDeployUpgradePanel({
  currentVersion,
  nextRelease,
  showPreviewUpgradeButton,
  onPreviewRelease,
  hasPermission,
  onOpenDeployDialog,
  workflowRunning,
}: WorkspaceDeployUpgradePanelProps) {
  const canDeployCsai = hasPermission('deploy_csai:workspace');
  const canDeployMai = hasPermission('deploy_mai:workspace');
  const canDeploySingleService = hasPermission('deploy_single_service:workspace');

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <DeployTile
        icon={<ArrowUpCircle className="h-4 w-4 text-muted-foreground" />}
        title="Upgrade"
        description={
          nextRelease
            ? `Upgrade from ${currentVersion || VERSION_UNKNOWN_LABEL} to ${nextRelease.version}.`
            : "You're on the latest available release."
        }
        action={
          nextRelease ? (
            showPreviewUpgradeButton && (
              <Button
                className="w-full"
                disabled={workflowRunning}
                onClick={() => onPreviewRelease?.(nextRelease.releaseId)}
              >
                Preview Upgrade
              </Button>
            )
          ) : (
            <Button className="w-full" disabled>
              Up to date
            </Button>
          )
        }
      />

      <DeployTile
        icon={<Layers className="h-4 w-4 text-muted-foreground" />}
        title="Deploy CSAI"
        description="Deploy the CSAI components to this workspace."
        action={
          canDeployCsai ? (
            <Button
              className="w-full"
              disabled={workflowRunning}
              onClick={() => onOpenDeployDialog('bais')}
            >
              Deploy
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">Contact an admin for access.</p>
          )
        }
      />

      <DeployTile
        icon={<Boxes className="h-4 w-4 text-muted-foreground" />}
        title="Deploy MAI"
        description="Deploy the MAI components to this workspace."
        action={
          canDeployMai ? (
            <Button
              className="w-full"
              disabled={workflowRunning}
              onClick={() => onOpenDeployDialog('mai')}
            >
              Deploy
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">Contact an admin for access.</p>
          )
        }
      />

      <DeployTile
        icon={<Rocket className="h-4 w-4 text-muted-foreground" />}
        title="Single Service Deploy"
        description="Deploy one service off an arbitrary branch, image, or chart version."
        action={
          canDeploySingleService ? (
            <Button
              className="w-full"
              disabled={workflowRunning}
              onClick={() => onOpenDeployDialog('single-service')}
            >
              Deploy
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">Contact an admin for access.</p>
          )
        }
      />
    </div>
  );
}
